/**
 * MeMark Synthesized Web Audio Engine v2.4
 * 
 * Provides crisp, tactile auditory feedback using the Web Audio API.
 * Synthesizes pure sound waves (Sine, Triangle, Square) across 4 selectable sound profiles.
 * 0 byte asset weight, 0 network requests, zero latency.
 */

import { playCrtDegauss, playVintageRelay, playFlybackPulse } from './crtAudioSynth';

let audioCtx = null;

export const SOUND_PROFILES = [
  { id: 'cyberpunk', name: 'Cyberpunk Mechanical', desc: 'Futuristic mechanical clicks and magnetic servo locks' },
  { id: 'arcade', name: '8-Bit Retro Arcade', desc: 'Chiptune square wave blips, power-up chimes and laser chirps' },
  { id: 'quantum', name: 'Quantum Glass', desc: 'High-frequency crystalline harmonics and glass chimes' },
  { id: 'stealth', name: 'Sub-Bass Stealth', desc: 'Warm low-frequency pulses and sub-bass haptic thuds' },
  { id: 'crt', name: 'Phosphor CRT Terminal', desc: 'Warm flyback power hum, magnetic degauss sweeps, and vintage solenoid clatter' },
];

export function getSoundProfile() {
  if (typeof window === 'undefined') return 'cyberpunk';
  return localStorage.getItem('memark_sound_profile') || 'cyberpunk';
}

export function setSoundProfile(profile) {
  if (typeof window !== 'undefined') {
    localStorage.setItem('memark_sound_profile', profile);
  }
}

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

export function isSoundMuted() {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('memark_sound_muted') === 'true';
}

export function toggleSoundMute() {
  const next = !isSoundMuted();
  if (typeof window !== 'undefined') {
    localStorage.setItem('memark_sound_muted', String(next));
  }
  return next;
}

export const toggleSound = toggleSoundMute;

/**
 * Plays a procedural sound effect adapted to the active sound profile.
 * @param {'click' | 'launch' | 'unlock' | 'lock' | 'copy' | 'delete' | 'success' | 'blip'} type
 */
export function playSound(type) {
  if (isSoundMuted()) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  const profile = getSoundProfile();
  const now = ctx.currentTime;

  try {
    switch (type) {
      case 'click':
      case 'blip': {
        if (profile === 'crt') {
          playVintageRelay();
          return;
        }
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        if (profile === 'arcade') {
          osc.type = 'square';
          osc.frequency.setValueAtTime(987.77, now); // B5
          osc.frequency.setValueAtTime(1318.51, now + 0.02); // E6
          gain.gain.setValueAtTime(0.04, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
        } else if (profile === 'quantum') {
          osc.type = 'sine';
          osc.frequency.setValueAtTime(1400, now);
          osc.frequency.exponentialRampToValueAtTime(800, now + 0.05);
          gain.gain.setValueAtTime(0.06, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
        } else if (profile === 'stealth') {
          osc.type = 'sine';
          osc.frequency.setValueAtTime(140, now);
          osc.frequency.exponentialRampToValueAtTime(60, now + 0.05);
          gain.gain.setValueAtTime(0.12, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
        } else {
          // Cyberpunk Mechanical default
          osc.type = 'triangle';
          osc.frequency.setValueAtTime(600, now);
          osc.frequency.exponentialRampToValueAtTime(120, now + 0.04);
          gain.gain.setValueAtTime(0.08, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.04);
        }

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.05);
        break;
      }

      case 'launch': {
        if (profile === 'crt') {
          playCrtDegauss();
          return;
        }
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        if (profile === 'arcade') {
          // 8-bit jump sound
          osc.type = 'square';
          osc.frequency.setValueAtTime(330, now);
          osc.frequency.linearRampToValueAtTime(880, now + 0.09);
          gain.gain.setValueAtTime(0.05, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.1);
        } else if (profile === 'quantum') {
          // Glass chime sweep
          osc.type = 'sine';
          osc.frequency.setValueAtTime(800, now);
          osc.frequency.exponentialRampToValueAtTime(1600, now + 0.1);
          gain.gain.setValueAtTime(0.06, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.14);
        } else if (profile === 'stealth') {
          // Deep sub sweep
          osc.type = 'sine';
          osc.frequency.setValueAtTime(80, now);
          osc.frequency.exponentialRampToValueAtTime(220, now + 0.1);
          gain.gain.setValueAtTime(0.14, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
        } else {
          // Cyberpunk default
          osc.type = 'sine';
          osc.frequency.setValueAtTime(440, now);
          osc.frequency.exponentialRampToValueAtTime(880, now + 0.08);
          gain.gain.setValueAtTime(0.09, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
        }

        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.15);
        break;
      }

      case 'copy': {
        [now, now + 0.05].forEach((t, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = profile === 'arcade' ? 'square' : 'sine';
          osc.frequency.setValueAtTime(i === 0 ? 800 : 1200, t);
          gain.gain.setValueAtTime(profile === 'arcade' ? 0.04 : 0.07, t);
          gain.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(t);
          osc.stop(t + 0.045);
        });
        break;
      }

      case 'unlock': {
        [523.25, 659.25, 783.99].forEach((freq, idx) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = profile === 'arcade' ? 'square' : 'sine';
          const start = now + idx * 0.04;
          osc.frequency.setValueAtTime(freq, start);
          gain.gain.setValueAtTime(0.05, start);
          gain.gain.exponentialRampToValueAtTime(0.001, start + 0.22);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(start);
          osc.stop(start + 0.23);
        });
        break;
      }

      case 'lock': {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = profile === 'arcade' ? 'square' : 'sine';
        osc.frequency.setValueAtTime(320, now);
        osc.frequency.exponentialRampToValueAtTime(140, now + 0.09);
        gain.gain.setValueAtTime(0.08, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.09);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.1);
        break;
      }

      case 'success': {
        [587.33, 739.99, 880.0, 1174.66].forEach((freq, idx) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = profile === 'arcade' ? 'square' : 'sine';
          const start = now + idx * 0.035;
          osc.frequency.setValueAtTime(freq, start);
          gain.gain.setValueAtTime(0.04, start);
          gain.gain.exponentialRampToValueAtTime(0.001, start + 0.28);
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.start(start);
          osc.stop(start + 0.3);
        });
        break;
      }

      default:
        break;
    }
  } catch (_) {
    // Ignore audio context synthesis issues
  }
}

export const playClick = () => playSound('click');
export const playSuccess = () => playSound('success');
export const playError = () => playSound('delete');
export { playCrtDegauss, playVintageRelay, playFlybackPulse };

